<script lang="ts" setup>
import { addStickyClass } from '~/utils/addStickyClass'
import { Routes } from '~/enums/Routes'
import getLocalizedRoute from '~/utils/getLocalizedRoute'

import Language from '~/components/Language.vue'
import LayoutEnum from '~/enums/LayoutEnum'
import HeaderTracking from '~/components/Header/HeaderTracking.vue'
import HeaderNews from '~/components/Header/HeaderNews.vue'
import { useHowTo } from '~/composables/useHowTo'
import { useStoreConfig } from '~/store/storeConfig'

const { hasAnyBlogPosts } = useStoreConfig()
const { getSlug } = useHowTo()
const howToSlug = ref<string | null>(getSlug())
const logos = await useAssetManager().getLogos()

const { type } = defineProps<{ type?: LayoutEnum.Market | LayoutEnum.Quote }>()

const headerRef = ref<HTMLDivElement>()
const placeholderRef = ref<HTMLDivElement>()

const scrollHandler = () => addStickyClass(headerRef.value, placeholderRef.value)

onMounted(() => {
  addStickyClass(headerRef.value)
  window?.addEventListener('scroll', scrollHandler)
})

onBeforeUnmount(() => {
  window?.removeEventListener('scroll', scrollHandler)
})
</script>

<template>
  <header :class="{ 'desktop-header': true, [type!]: Boolean(type) }">
    <section class="top-section px-2 lg:px-4">
      <div class="max-w-xlg m-auto flex items-center justify-between py-[5px] lg:px-[15px]">
        <div class="flex items-center">
          <Language />
          <DarkMode />
          <!-- TODO: Enable currency dropdown when needed -->
          <!-- <hr class="vertical-divider" />-->
          <!-- <Currency /> -->
        </div>
        <div class="flex items-center">
          <HeaderTracking />
          <hr class="vertical-divider" />
          <template v-if="howToSlug">
            <HeaderHowTo :slug="howToSlug" />
            <hr class="vertical-divider" />
          </template>
          <template v-if="hasAnyBlogPosts">
            <HeaderNews />
            <hr class="vertical-divider" />
          </template>
          <HeaderPro />
        </div>
      </div>
    </section>
    <section ref="headerRef" class="bottom-section flex justify-center py-2 px-2 lg:px-4">
      <div class="flex items-center max-w-xlg w-full lg:px-[15px]">
        <NuxtLink :to="getLocalizedRoute(Routes.Index)">
          <img :src="logos.logo" class="logo" alt="Logo" />
        </NuxtLink>
        <Navbar :type="type" />
        <SearchBar />
        <div class="profile flex items-center">
          <HeaderAccount />
          <HeaderCart />
        </div>
      </div>
    </section>
    <div ref="placeholderRef" class="header-placeholder" />
  </header>
</template>

<style lang="postcss" scoped>
.desktop-header {
  border-top: 5px solid theme('colors.brand.primary');
  border-bottom: 1px solid theme('colors.neutral6');
  background-color: theme('colors.white');
  width: 100%;

  .vertical-divider {
    box-sizing: border-box;
    margin: 0 1rem 0 1rem;
    height: 16px;
    width: 1px;
  }

  &.Quote {
    &,
    .sticky {
      border-top: 5px solid theme('colors.quoteYellow');
    }
  }

  &.Market {
    &,
    .sticky {
      border-top: 5px solid theme('colors.purple');
    }
  }

  .top-section {
    @apply border-b border-neutral6 dark:border-dmGray2;
  }

  .logo {
    min-width: 171px;
    height: 30px;
  }

  .bottom-section {
    .profile {
      gap: 15px;
      margin-left: auto;
      padding-left: 2rem;
    }
  }

  .sticky {
    position: fixed !important;
    z-index: 33;
    border-bottom: theme('vars.borderMedium');
    border-top: 5px solid theme('colors.brand.primary');
    width: 100%;
    background-color: theme('colors.white');
    left: 0;
    top: 0;
    padding: 0.5rem 0;

    & + .header-placeholder {
      height: 80px;
    }
  }
}

.dark {
  .desktop-header {
    &,
    :deep(.navbar) {
      background-color: theme('colors.dmGray1');

      .nav-item {
        color: theme('colors.dmText1') !important;

        &.active {
          color: theme('colors.black') !important;
        }
      }
    }
    &,
    :deep(.bottom-section) {
      border-color: theme('colors.dmGray2');
    }

    .bottom-section {
      .logo {
        @apply invert;
      }
    }
  }
  .sticky {
    background-color: theme('colors.dmGray1');
  }
}
</style>
